import React, {
  type ComponentProps,
  forwardRef,
  useCallback,
  useRef,
  useState,
} from 'react';
import type {
  DimensionValue,
  GestureResponderEvent,
  PressableProps,
  View,
  ViewProps,
} from 'react-native';
import { Pressable, StyleSheet } from 'react-native';
import { useStyle } from 'react-native-style-utilities';
import useMergedRef from '@react-hook/merged-ref';

import { theme } from '@garnish/constants';

import { usePressableState } from '../../../hooks';
import { Icon } from '../Icon';
import { webOnlyStyles } from '../../../utils';

export const IconLink = forwardRef<View, IconLinkProps>((props, ref) => {
  const {
    testID,
    accessibilityLabel,
    accessibilityHint,
    accessibilityRole,
    palette,
    style,
    disabled,
    width,
    height,
    iconSize,
    color,
    hitSlop,
    onPress,
    ...iconProps
  } = props;

  // ─── References ──────────────────────────────────────────────────────────────

  const localRef = useRef(null);
  const mergedRef = useMergedRef(localRef, ref);

  // ─── State ───────────────────────────────────────────────────────────

  const [isPressed, setPressed] = useState(false);
  const pressableState = usePressableState(localRef);

  const { isHovered, isActive, isFocused } = pressableState;
  const isLight = palette !== 'dark';

  const iconColor = getIconColor({
    disabled,
    isLight,
    isPressed,
    isHovered,
    isActive,
    isFocused,
  });

  // ─── Styles ──────────────────────────────────────────────────────────

  const dynamicStyle = useStyle(
    () => ({
      width,
      height,
      backgroundColor: getBackgroundColor({
        disabled,
        isLight,
        isHovered,
        isPressed,
      }),
    }),
    [width, height, isLight, isHovered, isPressed],
  );

  const pressableStyle = [
    styles.container,
    dynamicStyle,
    iconLinkWebStyles,
    isFocused ? iconLinkFocusedWebStyles : undefined,
    style,
  ];

  // ─── Callbacks ───────────────────────────────────────────────────────

  const onPressIn = useCallback(() => {
    setPressed(true);
  }, [setPressed]);
  const onPressOut = useCallback(() => {
    setPressed(false);
  }, [setPressed]);

  return (
    <Pressable
      ref={mergedRef}
      testID={testID ?? 'sg-icon-link'}
      accessibilityRole={accessibilityRole ?? 'button'}
      accessibilityLabel={accessibilityLabel}
      accessibilityHint={accessibilityHint}
      accessibilityState={{ disabled }}
      style={pressableStyle}
      disabled={disabled}
      hitSlop={hitSlop}
      onPress={onPress}
      onPressIn={onPressIn}
      onPressOut={onPressOut}
    >
      <Icon
        color={color ?? iconColor}
        {...(iconSize ? { width: iconSize, height: iconSize } : {})}
        {...iconProps}
      />
    </Pressable>
  );
});

/** Returns the icon color based on pressable state priorities. */
const getIconColor = ({
  disabled,
  isPressed,
  isActive,
  isFocused,
  isHovered,
  isLight,
}: IconColorSelection): string => {
  const colorMap = isLight ? IconLinkLightColorMap : IconLinkDarkColorMap;

  if (disabled) return colorMap.disabled;

  if (isPressed) return colorMap.pressed;

  if (isActive) return colorMap.active;

  if (isFocused) return colorMap.focus;

  if (isHovered) return colorMap.hovered;

  return colorMap.default;
};

/** Returns the background color based on pressable state priorities. */
const getBackgroundColor = ({
  disabled,
  isPressed,
  isHovered,
  isLight,
}: IconColorSelection): string => {
  const colorMap = isLight ? IconLinkLightColorMap : IconLinkDarkColorMap;

  if (!disabled && isPressed) return colorMap.pressedBackground;

  if (!disabled && isHovered) return colorMap.hoveredBackground;

  return colorMap.transparentBackground;
};

// ─── Types ───────────────────────────────────────────────────────────────────

type IconLinkProps = Readonly<{
  onPress?: ((event: GestureResponderEvent) => void) | null;
  palette?: 'dark' | 'light';
  disabled?: boolean;
  style?: ViewProps['style'];
  iconSize?: number;
  width?: DimensionValue;
  height?: DimensionValue;
}> &
  Omit<ComponentProps<typeof Icon>, 'style' | 'width' | 'height'> &
  Pick<
    PressableProps,
    | 'hitSlop'
    | 'accessibilityLabel'
    | 'accessibilityHint'
    | 'accessibilityRole'
    | 'testID'
  >;

type IconColorSelection = Readonly<{
  disabled?: boolean;
  isLight?: boolean;
  isPressed?: boolean;
  isHovered?: boolean;
  isActive?: boolean;
  isFocused?: boolean;
}>;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    borderRadius: 1000,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

const iconLinkWebStyles = webOnlyStyles({
  outlineColor: theme.colors.GRAY,
  outlineOffset: 2,
});
const iconLinkFocusedWebStyles = webOnlyStyles({
  outlineStyle: 'solid',
});

const IconLinkLightColorMap: Record<string, string> = {
  default: theme.colors.CHARCOAL,
  hovered: theme.colors.BLACK,
  pressed: theme.colors.BLACK,
  active: theme.colors.SPINACH,
  focus: theme.colors.CHARCOAL,
  disabled: theme.colors.OPACITY.GREEN_1.LIGHT,
  transparentBackground: theme.colors.OPACITY.TRANSPARENT,
  hoveredBackground: theme.colors.OPACITY.GREEN_1.ALMOST_TRANSPARENT,
  pressedBackground: theme.colors.OPACITY.GREEN_1.LIGHTEST,
};

const IconLinkDarkColorMap: Record<string, string> = {
  default: theme.colors.OATMEAL,
  hovered: theme.colors.OATMEAL,
  pressed: theme.colors.OATMEAL,
  active: theme.colors.AVOCADO,
  focus: theme.colors.OATMEAL,
  disabled: theme.colors.OPACITY.KHAKI_3.LIGHT,
  transparentBackground: theme.colors.OPACITY.TRANSPARENT,
  hoveredBackground: theme.colors.OPACITY.KHAKI_3.ALMOST_TRANSPARENT,
  pressedBackground: theme.colors.OPACITY.KHAKI_3.LIGHTEST,
};
